/* eslint-disable max-statements */
import React, { useEffect, useState, useCallback, createContext, useContext } from 'react';
import storeActions from 'Store/storeActions';
import { useStore } from 'Store';
import { Device } from 'DeviceUtils';
import { useTheme } from 'Theme';
import { getBackgroundByDevice } from 'Utils/backstageUtils';
import { getCustomOnboardingItems } from 'Utils/backstageWelcomeScreensUtils';
import { localeToLanguage } from '@24i/st-api-connector/utils';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import NavigationService from '../../Navigation/navigationService';

const AppEntryContext = createContext();

export const useAppEntryContext = () => useContext(AppEntryContext);

const AppEntryContextProvider = ({ children }) => {
    const { appManager, deviceStorageDispatch, setUserData, setCategories, setMCoreConfig } =
        useStore();
    const { setTheme } = useTheme();
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initializedData, setInitializedData] = useState(null);
    const navigation = useNavigation();

    const initialize = useCallback(async () => {
        setLoading(true);
        try {
            const { language } = await appManager.initialize();

            const deviceConfig = await appManager.getDeviceConfigData();
            let customOnboardingItems = [];
            // Ensure that language is correctly set, this is for backwards compatibility reason
            deviceStorageDispatch({
                type: storeActions.initState,
                payload: { ...deviceConfig, language }
            });

            const { theming, features } = (await appManager.getBackstageConfigData()) || {};
            if (theming) {
                setTheme({
                    ...theming,
                    backgroundImageUri: getBackgroundByDevice(theming.background)
                });
            }

            const userData = await appManager.getLoggedUserData();
            setUserData(userData);

            const { categories } = await appManager.getApi().getCategories(deviceConfig.language);
            setCategories(categories);

            const mCoreConfig = await appManager.getMCoreConfigData();
            setMCoreConfig(mCoreConfig);

            const menus = await appManager.getMenus();
            const { onboardingCompleted, showEnforceChangePassword } = deviceConfig;

            if (!Device.isWeb && !onboardingCompleted) {
                // Returns localised version of WelcomeScreens from API
                customOnboardingItems = await getCustomOnboardingItems(
                    appManager.getApi(),
                    features,
                    localeToLanguage(language)
                );
            }

            // If no items defined on BS or onboarding completeed skip
            const shouldSkipOnboarding = onboardingCompleted || customOnboardingItems.length === 0;
            setLoading(false);

            setInitializedData({
                ...initializedData,
                menus,
                onboardingCompleted: shouldSkipOnboarding,
                userData,
                showEnforceChangePassword,
                customOnboardingItems
            });
            setErrorMessage(null);
        } catch (error) {
            try {
                const language = await appManager.getAppPreferredLanguage();
                await appManager.initializeTranslations(language, []);
            } catch (err) {
                console.log('Error: appManager.initializeTranslations', err);
            }
            console.log('Error: initialize', error);
            setErrorMessage({
                errorCode: error?.errorCode || 500,
                localizedMessage:
                    error?.localizedMessage || 'Could not load app, please try again later'
            });
            setLoading(false);
        }
    }, [initializedData]);

    useEffect(() => {}, []);
    const isFocused = useIsFocused();
    useEffect(() => {
        if (isFocused) {
            const runInitialize = async () => {
                // NavigationService.setTopLevelNavigationInstance(navigation);
                await initialize();
            };

            runInitialize();
        }
        // initialize();
    }, [isFocused]);

    return (
        <AppEntryContext.Provider value={{ initializedData, errorMessage, initialize }}>
            {children}
        </AppEntryContext.Provider>
    );
};

export default AppEntryContextProvider;
